import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { likeTanzaku } from '../services/api';
import { getFontById, DEFAULT_FONT } from '../utils/fonts';
import './Modal.css';

const Modal = ({ tanzaku, onClose, onTanzakuLiked, onTanzakuDelete }) => {
  const [isLiking, setIsLiking] = useState(false);
  const [likeError, setLikeError] = useState('');
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const modalRef = useRef(null);
  
  // フォント情報を取得（フォールバック付き）
  const selectedFont = getFontById(tanzaku.font_id || DEFAULT_FONT.id);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleLike = async () => {
    if (isLiking) return;
    
    setIsLiking(true);
    setLikeError('');
    
    try {
      const updatedTanzaku = await likeTanzaku(tanzaku.id);
      onTanzakuLiked(updatedTanzaku);
      
      // いいねアニメーション
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 1000);
      
    } catch (err) {
      setLikeError('いいねできませんでした');
      setTimeout(() => setLikeError(''), 3000);
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = () => {
    if (onTanzakuDelete) {
      onTanzakuDelete(tanzaku.id);
    }
  };

  const getColorClass = (color) => {
    return `modal-tanzaku-${color}`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="modal-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={{ duration: 0.3 }}
      >
        <motion.div
          ref={modalRef}
          className={`modal-content ${getColorClass(tanzaku.color)}`}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <button 
            className="modal-close"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>

          <div className="modal-tanzaku">
            <div className="modal-tanzaku-header">
              <div className="modal-author-info">
                <h3 
                  className="modal-author-name"
                  style={{ fontFamily: selectedFont.fontFamily }}
                >
                  {tanzaku.author_name}
                </h3>
                <p className="modal-date">{formatDateTime(tanzaku.created_at)}</p>
              </div>
              <div className="modal-like-section">
                <motion.button
                  className={`modal-like-button ${isLiking ? 'liking' : ''}`}
                  onClick={handleLike}
                  disabled={isLiking}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="modal-heart">♥</span>
                  <span className="modal-like-count">{tanzaku.likes || 0}</span>
                </motion.button>
                
                {showLikeAnimation && (
                  <motion.div
                    className="modal-like-animation"
                    initial={{ opacity: 0, scale: 0.5, y: 0 }}
                    animate={{ opacity: 1, scale: 1.2, y: -30 }}
                    exit={{ opacity: 0, scale: 0.5, y: -50 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    +1 ♥
                  </motion.div>
                )}
              </div>
            </div>

            <div 
              className="modal-wish-text"
              style={{ fontFamily: selectedFont.fontFamily }}
            >
              {tanzaku.wish_text}
            </div>

            {likeError && (
              <motion.div 
                className="modal-like-error"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {likeError}
              </motion.div>
            )}

            <div className="modal-tanzaku-hole"></div>
            <div className="modal-tanzaku-string"></div>
          </div>

          <div className="modal-actions">
            <motion.button
              className="modal-delete-button"
              onClick={handleDelete}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              🗑️ 削除
            </motion.button>
            <motion.button
              className="modal-close-button"
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              閉じる
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Modal;